import { useContactInvalidation } from "@/hooks/use-contact-invalidation";
import {
  createContactNoteV1,
  deleteContactNoteV1,
  updateContactNoteV1,
} from "@/lib/data/requests";
import { type APIError } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";
import { type ContactSubitemMutationOptions } from "./contact-subitem-mutation-options";

export const useCreateContactNote = (
  options?: ContactSubitemMutationOptions,
) => {
  const invalidateContact = useContactInvalidation();
  return useMutation({
    mutationFn: (data: Parameters<typeof createContactNoteV1>[0]) =>
      createContactNoteV1(data),
    onError: () => {
      toast.error("Error creating contact note");
    },
    onSettled: async () => {
      await invalidateContact();
    },
    onSuccess: (_data, variables) => {
      if (options?.showSuccessToast === false) {
        return;
      }
      if (variables.draft) {
        toast.success("Note saved as draft");
        return;
      }
      toast.success("Contact note created successfully");
    },
  });
};

export const useUpdateContactNote = (
  options?: ContactSubitemMutationOptions,
) => {
  const invalidateContact = useContactInvalidation();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateContactNoteV1>[0]) =>
      updateContactNoteV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating contact note");
    },

    onSettled: async () => {
      await invalidateContact();
    },
    onSuccess: (_data, variables) => {
      if (options?.showSuccessToast === false) {
        return;
      }
      if (variables.draft) {
        toast.success("Note saved as draft");
        return;
      }
      toast.success("Contact note updated successfully");
    },
  });
};

export const useDeleteContactNote = (
  options?: ContactSubitemMutationOptions,
) => {
  const invalidateContact = useContactInvalidation();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteContactNoteV1>[0]) =>
      deleteContactNoteV1(data),
    onError: () => {
      toast.error("Error deleting contact note");
    },

    onSettled: async () => {
      await invalidateContact();
    },
    onSuccess: () => {
      if (options?.showSuccessToast !== false) {
        toast.success("Contact note deleted successfully");
      }
    },
  });
};
